import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Select, Table, Button, Popover } from 'antd';
import { productsService } from '../../services/ProductsService';
import { Link } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import Menu from '../../components/Menu/Menu';
import MobileBottomMenu from '../../components/MobileBottomMenu/MobileBottomMenu';
import MobileMenu from '../../components/Menu/MobileMenu';
import './ProductDetails.scss';
import { ClockCircleOutlined, EditOutlined } from '@ant-design/icons';
import productDetails from './details.json';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [productPrices, setProductPrices] = useState([]);  // Initialize as an empty array
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [pagination, setPagination] = useState({});
    const [loadingPrices, setLoadingPrices] = useState(false); // New loading state for product prices

    const getDefaultStartDate = () => {
        return startDate || moment().subtract(30, 'days').toDate();  // Default to 30 days ago
    };

    const getDefaultEndDate = () => {
        return endDate || moment().toDate();  // Default to today
    };

    useEffect(() => {
        // Fetch product details
        productsService.getProductDetails(id)
            .then(response => {
                if (response.status === 401) {
                    navigate('/login');
                } else {
                    setProduct(response.data);
                    setPagination(response.data.pagination);
                }
            })
            .catch(error => {
                setError('Failed to fetch product details.');
                console.error('Error fetching product details:', error);
            })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    useEffect(() => {
        // Check if the date range actually changed, otherwise don't trigger fetching again
        fetchProductPrices();
    }, [id, navigate]);

    const fetchProductPrices = () => {
        const formattedStart = moment(getDefaultStartDate()).format("YYYY-MM-DD");
        const formattedEnd = moment(getDefaultEndDate()).format("YYYY-MM-DD");

        setLoading(true);  // Indicate loading state
        setDateRange([getDefaultStartDate(), getDefaultEndDate()])

        productsService.getProductPrices(id, formattedStart, formattedEnd)
            .then(response => {
                if (response.status === 401) {
                    navigate('/login');
                } else {
                    if (response.data && response.data.length === 0) {
                        // Set empty array if no products are found in the selected range
                        setProductPrices([]);
                    } else {
                        setProductPrices(response.data || []);  // Update with the fetched prices
                    }
                }
            })
            .catch(error => {
                setError('Failed to fetch product prices.');
                console.error('Error fetching product prices:', error);
            })
            .finally(() => setLoading(false));  // End loading state
    };

    const handlePageClick = (data) => {
        productsService.getProductPrices(data.selected + 1)
            .then(response => {
                if (response.status === 401) {
                    navigate("/login");
                } else {
                    setProductPrices(response.data);
                    setPagination(response.data.pagination);
                }
            })
            .catch(error => {
                if (error.response && error.response.status === 401) {
                    navigate("/login");
                } else {
                    console.error('Error fetching data:', error);
                }
            });
    };

    const columns = [
        {
            title: 'FURNIZOR',
            dataIndex: 'supplierName',
            key: 'supplierName',
            render: (text, record) => (
                <div>
                    <h4>{record.supplierName}</h4>
                    <h5 className="product__table-date">{record.latestPurchaseDate}</h5>
                </div>
            ),
        },
        {
            title: 'PREȚ + TVA',
            dataIndex: 'priceWithVAT',
            key: 'priceWithVAT',
            render: (text, record) => (
                <Popover
                    content={<div><p>{record.priceWithoutVAT.toFixed(4)} RON</p></div>}
                    title="Preț fără TVA:"
                    trigger="click"
                >
                    <span style={{ cursor: 'pointer' }}>
                        <h4>{record.priceWithVAT.toFixed(3)} RON</h4>
                    </span>
                </Popover>
            ),
        },
        {
            title: 'ADAOS',
            dataIndex: 'adaos',
            key: 'adaos',
            render: (text, record) => <h4>{`${text}%`}</h4>,
        },
        {
            title: '',
            key: 'edit',
            render: (_, record) => (
                <Button type="link" icon={<img src="/images/edit.png" className="product__edit" />} />
            ),
        },
    ];

    const formattedProductPrices = Array.isArray(productPrices)
        ? productPrices.map(item => ({
            supplierName: item?.supplier?.name || 'N/A',
            adaos: item.comercial_adaos_percentage || 0,
            priceWithVAT: item?.purchase_price
                ? item.purchase_price * (1 + (item?.vat_percent ?? 0) / 100)
                : 0,
            priceWithoutVAT: item.purchase_price,
            latestPurchaseDate: item.latest_purchase_date,
        }))
        : []; // Ensure formattedProductPrices is always an array

    if (loading) return <div className="loading">Loading...</div>;
    if (error) return <div className="error">{error}</div>;
    if (!product) return <div className="error">Product not found.</div>;

    return (
        <div className="standard product standard__container">
            <div className="standard__mobile-header">
                <img src="/images/logo.png" alt="logo" className="standard__only-mobile standard__mobile-logo" />
                <MobileMenu />
            </div>

            <div className="product__back standard__only-mobile ">
                <div className="product__line" />
                <Link to="/search-product" className="standard__link">
                    <div className="product__back-box">
                        <img src="/images/back.png" alt="back" />
                        <h4>Detalii Produs</h4>
                    </div>
                </Link>
                <div className="product__line" />
            </div>
            <Menu />
            <div className='product__details-desktop-container'>

            <div className="product__back standard__only-desktop ">
                <Link to="/search-product" className="standard__link">
                    <div className="product__back-box">
                        <img src="/images/back.png" alt="back" />
                        <h4>Detalii Produs</h4>
                    </div>
                </Link>
                <div className="product__line" />
            </div>
            <div className="product__main-details">
                <h2 className="product__title">{product.name}</h2>
                <h4 className="product__price">{product.selling_price} RON</h4>
                <img src="/images/pie.png" alt="pie" />
                <img src="/images/settings.png" alt="settings" />
            </div>
            <div style={{ marginBottom: 16 }} className="product__date-picker-wrapper">
                <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => {
                      setDateRange(update);
                    }}
                    isClearable
                    onClear={() => {
                        setDateRange([null, null]);
                    }}
                    placeholderText="Select date range"
                    dateFormat="yyyy-MM-dd"
                    showDisabledMonthNavigation
                    dropdownMode="select"
                    className="products__date-picker"
                />
                <Button
                    type="primary"
                    size="large"
                    onClick={fetchProductPrices}
                >
                    Filtreaza
                </Button>
            </div>
            <div className="product__table">
                {productPrices.length === 0 && !loadingPrices ? (
                    <div>No products found for this date range.</div>  // Display message when no products are found
                ) : (
                    <Table
                        columns={columns}
                        dataSource={formattedProductPrices}
                        rowKey="provider"
                        pagination={false}
                        locale={{ emptyText: productPrices.length === 0 ? "Nu sunt detalii pentru acest produs" : "Loading..." }}
                    />
                )}
            </div>
            </div>

            <MobileBottomMenu />
            {pagination && pagination.last_page && (
                <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    breakClassName={'break-me'}
                    pageCount={pagination.last_page}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={1}
                    onPageChange={handlePageClick}
                    containerClassName={'pagination'}
                    subContainerClassName={'pages pagination'}
                    activeClassName={'active'}
                />
            )}
        </div>
    );
};

export default ProductDetails;
